import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { Observable } from 'rxjs';
import { PaginatedResponse } from '../../../shared/interfaces/pagination';
import { Auth0Client, Connection } from '../models/connection';
import { Group } from '../models/group';
import { ArcgisConfigurationAppExists } from '../models/gis-group';
import { CreateConnectionLink } from '../models/create-connection-link';
import { ConnectionLink } from '../models/connection-link';
import { Auth0Connection } from '../../../shared/models/auth0-connection';
import { UserInvitation } from '../models/user-invitation';

@Injectable({
  providedIn: 'root',
})
export class ManageConnectionService {
  private http: HttpClient = inject(HttpClient);

  private adminApiBaseUrl = environment.apiBaseUrl + '/api/admin';

  getAllConnections(
    page = 1,
    pageSize = 10,
  ): Observable<PaginatedResponse<Connection>> {
    const params = { page: page.toString(), page_size: pageSize.toString() };
    return this.http.get<PaginatedResponse<Connection>>(
      `${this.adminApiBaseUrl}/connections`,
      { params },
    );
  }

  getConnectionDetails(connectionName: string): Observable<Connection> {
    return this.http.get<Connection>(
      `${this.adminApiBaseUrl}/connections/${connectionName}/details`,
    );
  }

  createConnection(connection: Connection): Observable<Connection> {
    return this.http.post<Connection>(
      `${this.adminApiBaseUrl}/connections`,
      connection,
    );
  }

  updateConnection(connection: Connection): Observable<Connection> {
    return this.http.put<Connection>(
      `${this.adminApiBaseUrl}/connections/${connection.id}`,
      connection,
    );
  }

  patchConnectionHide(
    connectionName: string,
    hide: boolean,
  ): Observable<{ message: string; hide: boolean }> {
    return this.http.patch<{ message: string; hide: boolean }>(
      `${this.adminApiBaseUrl}/connections/${connectionName}/hide`,
      { hide },
    );
  }

  mapConnectionClient(connection: Connection): Observable<Connection> {
    return this.http.put<Connection>(
      `${this.adminApiBaseUrl}/connections/${connection.id}/clients`,
      connection,
    );
  }

  deleteConnection(
    connectionId: string,
    connectionType: number,
  ): Observable<HttpResponse<object>> {
    return this.http.delete(
      `${this.adminApiBaseUrl}/connections/${connectionId}?connection_type=${connectionType}`,
      {
        observe: 'response',
      },
    );
  }

  getConnectionGroups(connectionName: string): Observable<Group[]> {
    return this.http.get<Group[]>(
      `${this.adminApiBaseUrl}/connections/${connectionName}/groups`,
    );
  }

  getClients(): Observable<Auth0Client[]> {
    return this.http.get<Auth0Client[]>(
      `${this.adminApiBaseUrl}/auth0/clients`,
    );
  }

  createConnectionGroupMapping(
    connectionName: string,
    groupId: number,
  ): Observable<Connection> {
    return this.http.post<Connection>(
      `${this.adminApiBaseUrl}/connections/${connectionName}/groups/${groupId}`,
      undefined,
    );
  }

  deleteConnectionGroupMapping(
    connectionName: string,
    groupId: number,
  ): Observable<Connection> {
    return this.http.delete<Connection>(
      `${this.adminApiBaseUrl}/connections/${connectionName}/groups/${groupId}`,
      undefined,
    );
  }

  createConnectionLink(
    createConnectionLink: CreateConnectionLink,
  ): Observable<ConnectionLink> {
    const connectionLinkData = {
      ...createConnectionLink,
      validity: +createConnectionLink.validity,
      allowed_visits: +createConnectionLink.allowed_visits,
    };
    return this.http.post<ConnectionLink>(
      `${this.adminApiBaseUrl}/connections/link`,
      connectionLinkData,
    );
  }

  getConnectionLinks(): Observable<ConnectionLink[]> {
    return this.http.get<ConnectionLink[]>(
      `${this.adminApiBaseUrl}/connections/link`,
    );
  }

  deleteConnectionLink(linkId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.adminApiBaseUrl}/connections/link/${linkId}`,
    );
  }

  getConnectionsFromDB(): Observable<Auth0Connection[]> {
    return this.http.get<Auth0Connection[]>(
      this.adminApiBaseUrl + '/auth0connections',
    );
  }

  getArcgisConfigurationAppExists(
    connectionName: string,
  ): Observable<ArcgisConfigurationAppExists> {
    return this.http.get<ArcgisConfigurationAppExists>(
      `${this.adminApiBaseUrl}/connections/${connectionName}/arcgis/dashboard`,
    );
  }

  updateConnectionArcGISGroupMapping(
    connectionName: string,
    groupType: string,
  ): Observable<Connection> {
    return this.http.patch<Connection>(
      `${this.adminApiBaseUrl}/connections/${connectionName}/arcgis/dashboard/${groupType}`,
      { status: true },
    );
  }

  deleteConnectionArcGISGroupMapping(
    connectionName: string,
    groupType: string,
  ): Observable<Connection> {
    return this.http.patch<Connection>(
      `${this.adminApiBaseUrl}/connections/${connectionName}/arcgis/dashboard/${groupType}`,
      { status: false },
    );
  }

  // Invitation methods
  inviteUser(
    email: string,
    connectionName: string,
  ): Observable<UserInvitation> {
    return this.http.post<UserInvitation>(
      `${this.adminApiBaseUrl}/connections/${connectionName}/users/invite`,
      { email },
    );
  }

  getConnectionInvitations(
    connectionName: string,
  ): Observable<PaginatedResponse<UserInvitation>> {
    return this.http.get<PaginatedResponse<UserInvitation>>(
      `${this.adminApiBaseUrl}/connections/${connectionName}/users/invite?page_size=50`,
    );
  }
}
